import { css } from 'aphrodite';
import { useManagerPermissions } from '../../hooks/useManagerPermissions';
import { CoachingAdherenceScorecard } from '../../components/CallRecordingDetails/components/CoachingAdherenceScorecard';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { useCoachingConversationCallView } from '../../../queries/AIConversation/useCoachingConversationCallView';
import { styleSheet } from './adherenceDrawer.styles';
import { RubricBreakdown } from './RubricBreakdown';

interface IProps {
	conversationId: string | null;
	onClose?: () => void;
}

/**
 * Coaching-adherence content for the review drawer. Resolves the selected conversation's call view
 * (from the loaded list, or fetched by id for a deep link), gates to managers/admins, and branches
 * on the result shape: new records (`rubricBreakdown` present) render the points-based
 * {@link RubricBreakdown}; legacy records fall back to the original {@link CoachingAdherenceScorecard}.
 */
export function AdherenceDrawerContent({ conversationId, onClose }: IProps) {
	const { hasManagerPermissions, isLoading: permissionsLoading } = useManagerPermissions();
	const { callView, isLoading } = useCoachingConversationCallView(conversationId);

	if (isLoading || permissionsLoading) {
		return (
			<div className={css(styleSheet.stateSpinner)}>
				<LoadingSpinner type='small' />
			</div>
		);
	}

	if (!hasManagerPermissions) {
		return null;
	}

	if (!conversationId) {
		return <div className={css(styleSheet.stateWrap)}>Select a conversation to see its adherence breakdown.</div>;
	}

	const phoneCall = callView?.phoneCall ?? null;
	const result = phoneCall?.coachingAdherenceResult ?? null;

	if (!phoneCall || !result) {
		return <div className={css(styleSheet.stateWrap)}>No coaching adherence analysis found for this conversation.</div>;
	}

	if (result.rubricBreakdown) {
		return <RubricBreakdown result={result} onClose={onClose} />;
	}

	return (
		<div className={css(styleSheet.legacyWrap)}>
			<CoachingAdherenceScorecard phoneCall={phoneCall} />
		</div>
	);
}
